/**
 * KAN-713: вкладка "Прокси" в настройках: справочник прокси Telegram-аккаунтов
 * (название, адрес, тип, страна, продавец, заметка, последняя проверка, аккаунты
 * на прокси), действия "Проверить", "Изменить", "Удалить" и "Добавить прокси".
 */
import { useState } from 'react';
import { Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ConfirmDialog } from '@/components/telegram/ConfirmDialog';
import { ProxyCheckBadge } from '@/components/telegram/ProxyCheckBadge';
import { ProxyDirectoryDialog } from '@/components/telegram/ProxyDirectoryDialog';
import { useTelegramProxies, useTelegramProxyMutations } from '@/hooks/useTelegramProxies';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { formatRub } from '@/lib/money';
import { accountsCountLabel, proxyAddress } from '@/lib/telegramProxyDirectory';
import type { TelegramProxy } from '@/types/telegramProxy';

export const PROXIES_BLOCK_HELP =
  'Прокси для Telegram-аккаунтов. Выбираются из справочника при подключении и импорте аккаунта и в диалоге смены прокси. Проверка идёт с сервера: доступен ли прокси и Telegram через него.';
const EMPTY_TEXT = 'Прокси нет. Нажмите "Добавить прокси".';

type DialogState = { mode: 'create' } | { mode: 'edit'; proxy: TelegramProxy } | null;

interface RowProps {
  proxy: TelegramProxy;
  checking: boolean;
  checkError: string | null;
  onCheck: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

/** Число аккаунтов на прокси; по клику раскрывается список их названий. */
function ProxyAccountsCell({
  proxyId,
  accounts,
}: {
  proxyId: number;
  accounts: TelegramProxy['accounts'];
}): JSX.Element {
  const [open, setOpen] = useState(false);
  if (!accounts.length) return <span className="text-gray-400">нет</span>;
  const listId = `proxy-row-accounts-list-${proxyId}`;
  return (
    <div>
      <button
        type="button"
        className="text-blue-700 underline-offset-2 hover:underline"
        data-testid={`proxy-row-accounts-toggle-${proxyId}`}
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((v) => !v)}
      >
        {accountsCountLabel(accounts.length)}
      </button>
      {open && (
        <ul id={listId} className="mt-1 space-y-0.5" data-testid={listId}>
          {accounts.map((a) => (
            <li key={a.id}>{a.label || a.id}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ProxyRow({ proxy, checking, checkError, onCheck, onEdit, onDelete }: RowProps): JSX.Element {
  const id = proxy.id;
  return (
    <TableRow data-testid={`proxy-row-${id}`}>
      <TableCell className="text-sm font-medium">{proxy.label || '-'}</TableCell>
      <TableCell className="font-mono text-xs" data-testid={`proxy-row-address-${id}`}>
        {proxyAddress(proxy)}
      </TableCell>
      <TableCell className="text-xs">{proxy.proxy_type === 'http' ? 'HTTP' : 'SOCKS5'}</TableCell>
      <TableCell className="text-xs">{proxy.country}</TableCell>
      <TableCell className="text-xs">{proxy.seller_name}</TableCell>
      <TableCell className="whitespace-nowrap text-xs tabular-nums" data-testid={`proxy-row-cost-${id}`}>
        {proxy.monthly_cost_rub ? formatRub(proxy.monthly_cost_rub) : '-'}
      </TableCell>
      <TableCell className="max-w-[240px] whitespace-pre-wrap break-words text-xs text-gray-600">
        {proxy.notes}
      </TableCell>
      <TableCell>
        <ProxyCheckBadge result={proxy.last_check} testId={`proxy-row-check-${id}`} />
        {checkError && (
          <p className="mt-1 text-xs text-red-700" data-testid={`proxy-row-check-error-${id}`}>
            {checkError}
          </p>
        )}
      </TableCell>
      <TableCell className="text-xs" data-testid={`proxy-row-accounts-${id}`}>
        <ProxyAccountsCell proxyId={id} accounts={proxy.accounts} />
      </TableCell>
      <TableCell>
        <div className="flex gap-1">
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            data-testid={`proxy-row-check-btn-${id}`}
            disabled={checking}
            onClick={onCheck}
          >
            <RefreshCw className={`mr-1 h-3.5 w-3.5 ${checking ? 'animate-spin' : ''}`} />
            {checking ? 'Проверка...' : 'Проверить'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            data-testid={`proxy-row-edit-${id}`}
            onClick={onEdit}
          >
            <Pencil className="mr-1 h-3.5 w-3.5" />
            Изменить
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs text-red-700"
            data-testid={`proxy-row-delete-${id}`}
            onClick={onDelete}
          >
            <Trash2 className="mr-1 h-3.5 w-3.5" />
            Удалить
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export function ProxiesBlock(): JSX.Element {
  const { data, isLoading, isError } = useTelegramProxies();
  const { remove, checkSaved } = useTelegramProxyMutations();
  const proxies = data ?? [];
  const [dialog, setDialog] = useState<DialogState>(null);
  const [toDelete, setToDelete] = useState<TelegramProxy | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [checkingId, setCheckingId] = useState<number | null>(null);
  const [checkErrors, setCheckErrors] = useState<Record<number, string>>({});

  const runCheck = (proxy: TelegramProxy): void => {
    setCheckingId(proxy.id);
    setCheckErrors((prev) => {
      const next = { ...prev };
      delete next[proxy.id];
      return next;
    });
    checkSaved.mutate(proxy.id, {
      onError: (err) =>
        setCheckErrors((prev) => ({
          ...prev,
          [proxy.id]: extractErrorMessage(err, 'Не удалось проверить прокси'),
        })),
      onSettled: () => setCheckingId(null),
    });
  };

  const confirmDelete = (): void => {
    if (!toDelete) return;
    const proxy = toDelete;
    setToDelete(null);
    setDeleteError(null);
    remove.mutate(proxy.id, {
      onError: (err) =>
        setDeleteError(
          `${proxy.label || proxyAddress(proxy)}: ${extractErrorMessage(err, 'Не удалось удалить прокси')}`
        ),
    });
  };

  return (
    <section data-testid="proxies-block">
      <div className="mb-1 flex items-center gap-3">
        <h3 className="text-base font-semibold text-gray-900">Прокси</h3>
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2 text-xs"
          data-testid="proxy-add"
          onClick={() => setDialog({ mode: 'create' })}
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          Добавить прокси
        </Button>
      </div>
      <p className="mb-3 text-xs text-gray-500">{PROXIES_BLOCK_HELP}</p>
      {deleteError && (
        <p className="mb-2 text-xs text-red-700" data-testid="proxy-delete-error">
          {deleteError}
        </p>
      )}
      {isError && (
        <p className="text-sm text-red-700" data-testid="proxies-error">
          Не удалось загрузить прокси
        </p>
      )}
      {!isLoading && !isError && proxies.length === 0 && (
        <p className="text-sm text-gray-500" data-testid="proxies-empty">
          {EMPTY_TEXT}
        </p>
      )}
      {proxies.length > 0 && (
        <div className="overflow-x-auto rounded-lg border bg-white">
          <Table aria-label="Прокси">
            <TableHeader>
              <TableRow>
                <TableHead>Название</TableHead>
                <TableHead>Адрес</TableHead>
                <TableHead>Тип</TableHead>
                <TableHead>Страна</TableHead>
                <TableHead>Продавец</TableHead>
                <TableHead>В месяц</TableHead>
                <TableHead>Заметка</TableHead>
                <TableHead>Проверка</TableHead>
                <TableHead>Аккаунты</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {proxies.map((proxy) => (
                <ProxyRow
                  key={proxy.id}
                  proxy={proxy}
                  checking={checkingId === proxy.id}
                  checkError={checkErrors[proxy.id] ?? null}
                  onCheck={() => runCheck(proxy)}
                  onEdit={() => setDialog({ mode: 'edit', proxy })}
                  onDelete={() => setToDelete(proxy)}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <ProxyDirectoryDialog
        open={dialog !== null}
        onOpenChange={(open) => !open && setDialog(null)}
        proxy={dialog?.mode === 'edit' ? dialog.proxy : null}
      />
      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(open) => !open && setToDelete(null)}
        view={{
          title: 'Удалить прокси?',
          description: 'Карточка прокси будет удалена из справочника.',
          confirm: 'Удалить',
        }}
        extra={toDelete ? `${toDelete.label || proxyAddress(toDelete)}.` : null}
        onConfirm={confirmDelete}
        testId="proxy-delete-confirm"
      />
    </section>
  );
}

export default ProxiesBlock;
